'use client';

import { Badge, Button } from '@common/components';
import { captureException } from '@sentry/nextjs';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useIsGuest } from '@/hooks/useIsGuest';

import { useBenefitGate } from '../../_hooks/useBenefitGate';
import {
  useAttendance,
  useCheckInAttendance,
  useIsCheckingInAttendance,
} from '../_hooks/useAttendance';
import {
  AttendanceCloverStrip,
  AttendanceCloverStripPlaceholder,
} from './AttendanceCloverStrip';

type TriggerGate = ReturnType<typeof useBenefitGate>['triggerGate'];

//* 폴백만 그리고 끝내면 무성 실패다 — 이 화면이 조용히 게스트처럼 보였는데 Sentry에는
//* 아무것도 남지 않아 원인 추적이 막혔다. 두 번째 인자(componentStack)는 Sentry hint
//* 형식이 아니라 그대로 넘기지 않는다.
const reportBoundaryError = (error: Error) => captureException(error);

interface AttendanceCheckSectionProps {
  isGuest: boolean;
}

export const AttendanceCheckSection = ({
  isGuest: serverIsGuest,
}: AttendanceCheckSectionProps) => {
  const isGuest = useIsGuest(serverIsGuest);
  const { triggerGate, gateModals } = useBenefitGate();

  return (
    <section className="flex flex-col gap-16 bg-Gray-white p-20 xl:rounded-medium xl:border xl:border-Gray-100">
      {isGuest ? (
        <GuestAttendanceCheck triggerGate={triggerGate} />
      ) : (
        <MemberAttendanceCheck triggerGate={triggerGate} />
      )}
      {gateModals}
    </section>
  );
};

const GuestAttendanceCheck = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => (
  <>
    <div className="flex flex-col items-center gap-16">
      <Badge className="bg-O-25 text-O-600">모바일 앱 전용 혜택</Badge>
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="font-700 text-Gray-900 typo-h7">
          매일 미션 참여하고 웰포인트 받기
        </h2>
        <p className="text-Gray-600 typo-body4">
          웰로 앱에서만 만날 수 있는 혜택을 살펴보세요
        </p>
      </div>
    </div>
    <AttendanceCloverStrip variant="guest" />
    <Button
      className="w-full"
      dimensions="H48"
      onClick={() => triggerGate(['login'], () => {})}
    >
      혜택받기
    </Button>
  </>
);

const AttendanceCurrentDay = () => {
  const { data } = useAttendance();

  return <>{data.todayDayIndex}</>;
};

const MemberCloverStrip = () => {
  const { data } = useAttendance();

  //* buttonState는 멀쩡한데 days만 빈 응답이 실제로 나갔다. 그냥 그리면 "출석 완료"
  //* 버튼 옆에 미출석 스트립이 서는 자기모순 화면이 되므로 실패로 승격해 보고한다.
  if (!data.cloverDays.length) {
    throw new Error('출석 캘린더(days)가 비어 있습니다');
  }

  return (
    <AttendanceCloverStrip
      cloverDays={data.cloverDays}
      todayDayIndex={data.todayDayIndex}
    />
  );
};

const BUTTON_LABEL_BY_STATE = {
  CHECK: '출석체크하기',
  RESTART: '1일부터 다시하기',
  DONE: '출석완료',
} as const;

const AttendanceCheckInButton = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => {
  const { data } = useAttendance();
  const { mutate: checkIn } = useCheckInAttendance();
  const isCheckingIn = useIsCheckingInAttendance();

  const handleCheckIn = () =>
    triggerGate(['login', 'adult', 'consent'], () => checkIn({}));

  return (
    <Button
      className="w-full"
      dimensions="H48"
      disabled={
        data.buttonState === 'DONE' || data.alreadyCheckedToday || isCheckingIn
      }
      onClick={handleCheckIn}
    >
      {BUTTON_LABEL_BY_STATE[data.buttonState]}
    </Button>
  );
};

const MemberAttendanceCheck = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => (
  <>
    <div className="flex flex-col items-center gap-16">
      <Badge className="bg-O-25 text-O-600">
        <ErrorBoundary fallback={<>-</>} onError={reportBoundaryError}>
          <Suspense fallback="-">
            <AttendanceCurrentDay />
          </Suspense>
        </ErrorBoundary>
        일차 진행중
      </Badge>
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="font-700 text-Gray-900 typo-h7">
          연속 출석하고 행운권 받기
        </h2>
        <p className="text-Gray-600 typo-body4">7일 연속 방문 시 행운권 3배</p>
      </div>
    </div>
    <ErrorBoundary
      fallback={
        <AttendanceCloverStripPlaceholder message="출석 현황을 불러오지 못했어요" />
      }
      onError={reportBoundaryError}
    >
      <Suspense fallback={<AttendanceCloverStripPlaceholder />}>
        <MemberCloverStrip />
      </Suspense>
    </ErrorBoundary>
    <ErrorBoundary
      fallback={
        <Button className="w-full" dimensions="H48" disabled>
          출석체크하기
        </Button>
      }
      onError={reportBoundaryError}
    >
      <Suspense
        fallback={
          <Button className="w-full" dimensions="H48" disabled>
            출석체크하기
          </Button>
        }
      >
        <AttendanceCheckInButton triggerGate={triggerGate} />
      </Suspense>
    </ErrorBoundary>
  </>
);
